import { useTranslation } from 'react-i18next';

/**
 * Hook to get a localized field value from an object.
 * Looks for fieldName_<locale> first, then falls back to fieldName.
 * 
 * Example usage:
 *   const getLocalizedName = useLocalizedField();
 *   const name = getLocalizedName(raceDistance, 'displayName');
 *   // Returns displayName_no if locale is 'no' and field exists,
 *   // otherwise returns displayName
 */
export function useLocalizedField() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.substring(0, 2) || 'no';

  return function getLocalizedField<T extends Record<string, any>>(
    obj: T,
    fieldName: keyof T & string
  ): string {
    if (!obj) return '';
    
    const candidates = [
      `${fieldName}_${locale}`,
      `${fieldName}_en`,
      `${fieldName}_no`,
      fieldName
    ] as const;

    for (const key of candidates) {
      const value = obj[key as keyof T];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return '';
  };
}

export default useLocalizedField;
